<template>
  <div class="min-h-screen">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header Section -->
      <div class="mb-8">
        <div class="flex items-center justify-between element-island">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">
              {{ $t('AuditLog.title') }}
            </h1>

            <p class="mt-2 text-sm text-gray-600">
              {{ $t('AuditLog.subtitle') }}
            </p>
          </div>

          <div
            v-if="projectsStore.project"
            class="flex items-center space-x-2"
          >
            <p>
              <span class="text-sm text-gray-500">{{ $t('AuditLog.retentionPeriod') }}&nbsp;</span>

              <span
                v-if="projectsStore.project.log_retention_period === -1"
                class="text-sm font-medium text-gray-900"
              >{{ $t('AuditLog.infinite') }}</span>

              <span
                v-else
                class="text-sm font-medium text-gray-900"
              >{{ projectsStore.project.log_retention_period }} {{ $t('AuditLog.days') }}</span>
            </p>
          </div>
        </div>
      </div>

      <!-- Main Content Card -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <!-- Table Header with Search -->
        <div class="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div class="space-y-4">
            <!-- Filter Header -->
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
 class="w-5 h-5 text-gray-600">
    <path
    stroke-linecap="round"
    stroke-linejoin="round"
    stroke-width="2"
    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
    />
</svg>

                <h3 class="text-lg font-semibold text-gray-900">
                  {{ $t('AuditLog.filters') }}
                </h3>
              </div>

              <Transition
                mode="out-in"
                enter-active-class="animate__animated animate__fadeIn animate__faster"
                leave-active-class="animate__animated animate__fadeOut animate__faster"
              >
                <p
                  v-if="audits"
                  key="total-entries"
                  class="text-sm text-gray-600 bg-white px-3 py-1 rounded-full border border-gray-200"
                >
                  <span class="font-medium text-gray-900">{{ audits.meta.total }}&nbsp;</span>

                  <span class="text-gray-500">{{ $t('AuditLog.totalEntries') }}</span>
                </p>
              </Transition>
            </div>

            <!-- Filter Controls -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <!-- Log Type Filter -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">
                  {{ $t('AuditLog.logType') }}
                </label>

                <AppSelect
                  v-model="filterForm.form.log_name"
                  name="log_name_filter"
                  class="w-full capitalize form-input pr-8 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  :options="filteredLogNameOptions"
                  :empty-text="$t('AuditLog.allLogTypes')"
                />
              </div>

              <!-- Log Level Filter -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">
                  {{ $t('AuditLog.logLevel') }}
                </label>

                <AppSelect
                  v-model="filterForm.form.log_level"
                  name="log_level_filter"
                  class="w-full capitalize form-input pr-8 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  :options="logLevelOptions"
                  :empty-text="$t('AuditLog.allLevels')"
                />
              </div>

              <!-- User Filter -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">
                  {{ $t('AuditLog.user') }}
                </label>

                <AppTypeAhead
                  ref="typeAheadRef"
                  v-model="userName"
                  name="user_filter"
                  class="w-full"
                  :placeholder="$t('AuditLog.searchUsers')"
                  :suggestion-callback="getSuggestions"
                  :initial-suggestions="getSuggestions"
                  @value-selected="selectUser"
                >
                  <template #icon>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
</svg>
                  </template>
                </AppTypeAhead>
              </div>

              <!-- Clear Filters Button -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700 opacity-0">
                  {{ $t('AuditLog.actions') }}
                </label>

                <button
                  type="button"
                  class="btn btn--secondary-blue w-full text-sm"
                  @click="resetFilters"
                >
                  {{ $t('AuditLog.clearFilters') }}
                </button>
              </div>
            </div>

            <!-- Active Filters Display -->
            <div
              v-if="hasActiveFilters"
              class="flex items-center space-x-2 pt-2"
            >
              <span class="text-sm text-gray-500">{{ $t('AuditLog.activeFilters') }}</span>

              <div class="flex flex-wrap gap-2">
                <span
                  v-if="filterForm.form.log_name"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="getModelBadgeColour(filterForm.form.log_name).label"
                >
                  {{ getLogNameLabel(filterForm.form.log_name) }}
                  <button
                    type="button"
                    class="ml-1.5"
                    :class="getModelBadgeColour(filterForm.form.log_name).filter"
                    @click="filterForm.form.log_name = undefined"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-3 h-3">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
</svg>
                  </button>
                </span>

                <span
                  v-if="filterForm.form.log_level"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="getLogLevelColour(filterForm.form.log_level).label"
                >
                  {{ getLogLevelLabel(filterForm.form.log_level) }}
                  <button
                    type="button"
                    class="ml-1.5"
                    :class="getLogLevelColour(filterForm.form.log_level).filter"
                    @click="filterForm.form.log_level = undefined"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-3 h-3">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
</svg>
                  </button>
                </span>

                <span
                  v-if="filterForm.form.user_id"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-300 text-gray-700"
                >
                  {{ selectedUserName }}
                  <button
                    type="button"
                    class="ml-1.5 text-gray-600 hover:text-gray-800"
                    @click="clearUserFilter"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-3 h-3">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
</svg>
                  </button>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination Top -->
        <div class="px-6 py-4 border-b border-gray-200 bg-white">
          <Pagination
            :audits="audits"
            :loading="indexActivityLogAction.is(States.LOADING)"
            @go-to-page="goToPage($event.page)"
          />
        </div>

        <!-- Table -->
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {{ $t('AuditLog.log') }}
                </th>

                <th class="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {{ $t('AuditLog.user') }}
                </th>

                <th class="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {{ $t('AuditLog.date') }}
                </th>
              </tr>
            </thead>

            <tbody class="bg-white divide-y divide-gray-100">
              <!-- Loading State -->
              <tr v-if="!indexActivityLogAction.firstLoad.value && indexActivityLogAction.is(States.LOADING)">
                <td
                  colspan="3"
                  class="px-6 py-16 text-center"
                >
                  <div class="flex flex-col items-center">
                    <AppSpinner class="h-8 w-8 mb-4" />

                    <h3 class="text-sm font-medium text-gray-900 mb-1">
                      {{ $t('AuditLog.loading') }}
                    </h3>
                  </div>
                </td>
              </tr>

              <!-- Data Rows -->
              <template v-else-if="(indexActivityLogAction.is(States.COMPLETE) || indexActivityLogAction.firstLoad.value) && audits">
                <tr
                  v-for="audit in audits.data"
                  :key="audit.id"
                  class="hover:bg-gray-50 transition-colors duration-150 ease-in-out"
                  :class="{ 'opacity-50 pointer-events-none': indexActivityLogAction.is(States.LOADING) }"
                >
                  <td class="px-6 py-5">
                    <div class="flex items-start justify-between">
                      <div class="flex items-center space-x-2 flex-1">
                        <span
                          class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize flex-shrink-0"
                          :class="getModelBadgeColour(audit.log_name).label"
                        >
                          <span
                            class="w-1.5 h-1.5 rounded-full mr-1.5"
                            :class="getModelBadgeColour(audit.log_name).dot"
                          />
                          {{ audit.log_name.replace(/_/g, ' ') }}
                        </span>

                        <div
                          class="text-sm text-gray-900 leading-relaxed"
                          v-html="audit.description"
                        />
                      </div>

                      <!-- Log Level Label -->
                      <div
                        v-if="audit.properties?.level"
                        class="ml-4 flex-shrink-0"
                      >
                        <span
                          class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium uppercase tracking-wide"
                          :class="getLogLevelColour(audit.properties.level).label"
                        >
                          <span
                            class="w-1.5 h-1.5 rounded-full mr-1.5"
                            :class="getLogLevelColour(audit.properties.level).dot"
                          />
                          {{ audit.properties.level }}
                        </span>
                      </div>
                    </div>

                    <!-- Media preview -->
                    <!-- <div
                         v-if="audit.log_name === 'media' && audit.subject?.generated_conversions?.thumb"
                         class="mt-3"
                       >
                         <img
                           :src="audit.subject.getFullUrl('thumb')"
                           class="w-16 h-16 object-cover rounded-lg border border-gray-200 shadow-sm"
                           alt="Media thumbnail"
                         />
                       </div> -->
                  </td>

                  <td class="px-6 py-5 whitespace-nowrap">
                    <div class="flex items-center">
                      <div class="flex-shrink-0 h-9 w-9">
                        <div class="h-9 w-9 rounded-full bg-gray-200 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-if="audit.causer"
                           
                            class="h-4 w-4 text-gray-600">
  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
</svg>

                          <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" v-else
                           
                            class="h-4 w-4 text-gray-600">
    <path d="M9 15C8.44771 15 8 15.4477 8 16C8 16.5523 8.44771 17 9 17C9.55229 17 10 16.5523 10 16C10 15.4477 9.55229 15 9 15Z"/>
    <path d="M14 16C14 15.4477 14.4477 15 15 15C15.5523 15 16 15.4477 16 16C16 16.5523 15.5523 17 15 17C14.4477 17 14 16.5523 14 16Z"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M12 1C10.8954 1 10 1.89543 10 3C10 3.74028 10.4022 4.38663 11 4.73244V7H6C4.34315 7 3 8.34315 3 10V20C3 21.6569 4.34315 23 6 23H18C19.6569 23 21 21.6569 21 20V10C21 8.34315 19.6569 7 18 7H13V4.73244C13.5978 4.38663 14 3.74028 14 3C14 1.89543 13.1046 1 12 1ZM5 10C5 9.44772 5.44772 9 6 9H7.38197L8.82918 11.8944C9.16796 12.572 9.86049 13 10.618 13H13.382C14.1395 13 14.832 12.572 15.1708 11.8944L16.618 9H18C18.5523 9 19 9.44772 19 10V20C19 20.5523 18.5523 21 18 21H6C5.44772 21 5 20.5523 5 20V10ZM13.382 11L14.382 9H9.61803L10.618 11H13.382Z"/>
    <path d="M1 14C0.447715 14 0 14.4477 0 15V17C0 17.5523 0.447715 18 1 18C1.55228 18 2 17.5523 2 17V15C2 14.4477 1.55228 14 1 14Z"/>
    <path d="M22 15C22 14.4477 22.4477 14 23 14C23.5523 14 24 14.4477 24 15V17C24 17.5523 23.5523 18 23 18C22.4477 18 22 17.5523 22 17V15Z"/>
</svg>
                        </div>
                      </div>

                      <div class="ml-4">
                        <div class="text-sm font-medium text-gray-900">
                          {{ audit.causer ? audit.causer.name : $t('AuditLog.system') }}
                        </div>

                        <div class="text-sm text-gray-500">
                          {{ getCauserCategory(audit) }}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td class="px-6 py-5 whitespace-nowrap text-sm text-gray-900">
                    <div class="space-y-1">
                      <div class="font-medium text-gray-900">
                        {{ formatTimeAgo(audit.created_at) }}
                      </div>

                      <div class="text-gray-500 text-xs">
                        {{ formatDateTime(audit.created_at) }}
                      </div>
                    </div>
                  </td>
                </tr>

                <!-- Empty State -->
                <tr v-if="audits.meta.total === 0">
                  <td
                    colspan="3"
                    class="px-6 py-16 text-center"
                  >
                    <div class="flex flex-col items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="mx-auto h-12 w-12 text-gray-300 mb-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

                      <h3 class="text-sm font-medium text-gray-900 mb-1">
                        {{ $t('AuditLog.noLogsFound') }}
                      </h3>

                      <p
                        v-if="hasActiveFilters"
                        class="text-sm text-gray-500"
                      >
                        {{ $t('AuditLog.adjustFilters') }}
                      </p>

                      <p
                        v-else
                        class="text-sm text-gray-500"
                      >
                        {{ $t('AuditLog.getStarted') }}
                      </p>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- Pagination Bottom -->
        <div class="px-6 py-4 border-t border-gray-200 bg-white">
          <Pagination
            :audits="audits"
            :loading="indexActivityLogAction.is(States.LOADING)"
            @go-to-page="goToPage($event.page)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Pagination from '@/components/AuditLog/Pagination.vue';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { t } from '@/i18n';
import ActivityLogService from '@/services/ActivityLogService';
import { useProjectsStore } from '@/store/projects';
import { useDebounceFn } from '@vueuse/core';
import { useRouteQuery } from '@vueuse/router';
import { DateTime } from 'luxon';
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  projectId: string;
}>();

const pageQuery = useRouteQuery('page', '1', { transform: Number });
const audits = ref<ResourcePaginator<AuditLog>>(undefined);
const projectsStore = useProjectsStore();
const router = useRouter();
const indexActivityLogAction = useStoreApiAction(ActivityLogService.index);
const typeAheadRef = useTemplateRef('typeAheadRef');

// Filter form
const filterForm = useFormData({
  log_name: undefined,
  log_level: undefined,
  user_id: undefined,
});

const userName = ref('');
const selectedUserName = ref('');

watch(userName, (newValue) => {
  filterForm.resetData(['user_id']);
});

// Filter options
const logNameOptions = computed(() => [
  { value: 'company', label: t('Models.Company.logNameLabel') },
  { value: 'daily_quantity', label: t('Models.DailyQuantity.logNameLabel') },
  { value: 'delay', label: t('Models.Delay.logNameLabel') },
  { value: 'labour', label: t('Models.Labour.logNameLabel') },
  { value: 'labour_daily_record', label: t('Models.LabourDailyRecord.logNameLabel') },
  { value: 'package', label: t('Models.Package.logNameLabel') },
  { value: 'plant', label: t('Models.Plant.logNameLabel') },
  { value: 'plant_daily_record', label: t('Models.PlantDailyRecord.logNameLabel') },
  { value: 'progress_claim', label: t('Models.Claim.logNameLabel') },
  { value: 'project', label: t('Models.Project.logNameLabel') },
  { value: 'trade', label: t('Models.Trade.logNameLabel') },
  { value: 'trade_item', label: t('Models.TradeItem.logNameLabel') },
  { value: 'vendor', label: t('Models.ProjectSupplier.logNameLabel') },
  { value: 'variation_order', label: t('Models.VariationOrder.logNameLabel') },
]);

// Computed property to filter log name options based on project type
const filteredLogNameOptions = computed(() => {
  if(projectsStore.project?.isContractor) {
    return logNameOptions.value;
  }

  // Hide specific options for client projects
  const hiddenOptions = ['labour', 'plant', 'plant_daily_record', 'labour_daily_record', 'daily_quantity'];

  return logNameOptions.value.filter((option) => !hiddenOptions.includes(option.value));
});

const logLevelOptions = computed(() => [
  { value: 'info', label: t('AuditLog.logLevels.info') },
  { value: 'warning', label: t('AuditLog.logLevels.warning') },
  { value: 'alert', label: t('AuditLog.logLevels.alert') },
  { value: 'critical', label: t('AuditLog.logLevels.critical') },
]);

const suggestedUsers = ref<{ id: number; name: string }[]>([]);

// User search functionality
const getSuggestions = async () => {
  suggestedUsers.value = await projectsStore
    .searchUsers(props.projectId, { name: userName.value });

  return suggestedUsers.value.map((value) => value.name);
};

const selectUser = (index: number) => {
  nextTick(() => {
    if(suggestedUsers.value[index]) {
      filterForm.form.user_id = suggestedUsers.value[index].id;
      selectedUserName.value = suggestedUsers.value[index].name;
    }
  });
};

const resetFilters = () => {
  userName.value = '';
  selectedUserName.value = '';
  filterForm.resetData();
};

const applyFilters = () => {
  router.replace({
    query: {
      page: 1,
      ...filterForm.form,
      userName: filterForm.form.user_id ? userName.value : undefined,
    },
  });

  indexActivityLogAction.request(props.projectId, { page: 1, ...filterForm.form }).then(
    ([data, error]) => {
      if(data) {
        audits.value = data;
      }
    },
  );
};

const debouncedApplyFilters = useDebounceFn(applyFilters, 300);

watch(() => props.projectId, async (newValue) => {
  // Load audit data
  if(newValue) {
    await router.isReady();

    filterForm.form.log_name = router.currentRoute.value.query.log_name as string || undefined;
    filterForm.form.log_level = router.currentRoute.value.query.log_level as string || undefined;

    userName.value = router.currentRoute.value.query.userName as string || '';
    selectedUserName.value = router.currentRoute.value.query.userName as string || '';

    nextTick(() => {
      filterForm.form.user_id = router.currentRoute.value.query.user_id ?
        parseInt(router.currentRoute.value.query.user_id as string) :
        undefined;

      if(filterForm.form.user_id) {
        typeAheadRef.value?.setSelectedIndex(0);
      }

      applyFilters();
    });
  }
}, { immediate: true });

// Watch for filter changes
watch(filterForm.form, () => {
  debouncedApplyFilters();
}, { deep: true });

// Computed properties for filter display
const hasActiveFilters = computed(() => {
  return filterForm.form.log_name || filterForm.form.log_level || filterForm.form.user_id;
});

const getLogNameLabel = (logName: string): string => {
  const option = logNameOptions.value.find((opt) => opt.value === logName);

  return option ? option.label : logName;
};

const getLogLevelLabel = (logLevel: string): string => {
  const option = logLevelOptions.value.find((opt) => opt.value === logLevel);

  return option ? option.label : logLevel;
};

// Badge color mapping
const getModelBadgeColour = (logName: string): { label: string; dot: string; filter: string } => {
  const colorMap: Record<string, { label: string; dot: string; filter: string }> = {
    project: {
      label: 'bg-blue-100 text-blue-800',
      dot: 'bg-blue-400',
      filter: 'text-blue-600 hover:text-blue-800',
    },

    company: {
      label: 'bg-purple-100 text-purple-800',
      dot: 'bg-purple-400',
      filter: 'text-purple-600 hover:text-purple-800',
    },

    user: {
      label: 'bg-cyan-100 text-cyan-800',
      dot: 'bg-cyan-400',
      filter: 'text-cyan-600 hover:text-cyan-800',
    },

    system: {
      label: 'bg-yellow-100 text-yellow-800',
      dot: 'bg-yellow-400',
      filter: 'text-yellow-600 hover:text-yellow-800',
    },

    progress_claim: {
      label: 'bg-green-100 text-green-800',
      dot: 'bg-green-400',
      filter: 'text-green-600 hover:text-green-800',
    },

    plant: {
      label: 'bg-orange-100 text-orange-800',
      dot: 'bg-orange-400',
      filter: 'text-orange-600 hover:text-orange-800',
    },

    labour: {
      label: 'bg-indigo-100 text-indigo-800',
      dot: 'bg-indigo-400',
      filter: 'text-indigo-600 hover:text-indigo-800',
    },

    trade: {
      label: 'bg-teal-100 text-teal-800',
      dot: 'bg-teal-400',
      filter: 'text-teal-600 hover:text-teal-800',
    },

    trade_item: {
      label: 'bg-pink-100 text-pink-800',
      dot: 'bg-pink-400',
      filter: 'text-pink-600 hover:text-pink-800',
    },

    vendor: {
      label: 'bg-emerald-100 text-emerald-800',
      dot: 'bg-emerald-400',
      filter: 'text-emerald-600 hover:text-emerald-800',
    },

    delay: {
      label: 'bg-red-100 text-red-800',
      dot: 'bg-red-400',
      filter: 'text-red-600 hover:text-red-800',
    },

    labour_daily_record: {
      label: 'bg-indigo-100 text-indigo-800',
      dot: 'bg-indigo-400',
      filter: 'text-indigo-600 hover:text-indigo-800',
    },

    package: {
      label: 'bg-amber-100 text-amber-800',
      dot: 'bg-amber-400',
      filter: 'text-amber-600 hover:text-amber-800',
    },

    plant_daily_record: {
      label: 'bg-orange-100 text-orange-800',
      dot: 'bg-orange-400',
      filter: 'text-orange-600 hover:text-orange-800',
    },

    daily_quantity: {
      label: 'bg-lime-100 text-lime-800',
      dot: 'bg-lime-400',
      filter: 'text-lime-600 hover:text-lime-800',
    },

    variation_order: {
      label: 'bg-purple-100 text-purple-800',
      dot: 'bg-purple-400',
      filter: 'text-purple-600 hover:text-purple-800',
    },

    report: {
      label: 'bg-violet-100 text-violet-800',
      dot: 'bg-violet-400',
      filter: 'text-violet-600 hover:text-violet-800',
    },
  };

  return colorMap[logName] ?? {
    label: 'bg-gray-100 text-gray-800',
    dot: 'bg-gray-400',
    filter: 'text-gray-600 hover:text-gray-800',
  };
};

// Log level color mapping
const getLogLevelColour = (level: string): { label: string; dot: string; filter: string } => {
  const colorMap: Record<string, { label: string; dot: string; filter: string }> = {
    info: {
      label: 'bg-blue-50 text-blue-700 border border-blue-200',
      dot: 'bg-blue-500',
      filter: 'text-blue-600 hover:text-blue-800',
    },

    warning: {
      label: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
      dot: 'bg-yellow-500',
      filter: 'text-yellow-600 hover:text-yellow-800',
    },

    alert: {
      label: 'bg-red-50 text-red-700 border border-red-200',
      dot: 'bg-red-500',
      filter: 'text-red-600 hover:text-red-800',
    },

    critical: {
      label: 'bg-red-100 text-red-800 border border-red-300',
      dot: 'bg-red-600',
      filter: 'text-red-600 hover:text-red-800',
    },
  };

  return colorMap[level] ?? {
    label: 'bg-gray-50 text-gray-700 border border-gray-200',
    dot: 'bg-gray-500',
    filter: 'text-gray-600 hover:text-gray-800',
  };
};

// Format time ago using Luxon (similar to diffForHumans)
const formatTimeAgo = (dateString: string): string => {
  return DateTime.fromISO(dateString).toRelative();
};

// Format date time using Luxon
const formatDateTime = (dateString: string): string => {
  const date = DateTime.fromISO(dateString);

  return date.toFormat('dd/MM/yyyy h:mm a');
};

// Pagination functions
const goToPage = (page: number) => {
  pageQuery.value = page;

  indexActivityLogAction.request(props.projectId, { page: pageQuery.value, ...filterForm.form }).then(
    ([data, error]) => {
      if(data) {
        audits.value = data;
      }
    },
  );
};

const getCauserCategory = (auditLog: AuditLog) => {
  if(!auditLog.causer) {
    return t('AuditLog.automated');
  }

  if(auditLog.properties.admin_action) {
    return t('AuditLog.admin');
  }

  return t('AuditLog.user');
};

const clearUserFilter = () => {
  userName.value = '';
  selectedUserName.value = '';
  filterForm.form.user_id = undefined;
};
</script>

<style scoped></style>
